import { useEffect, useState } from "react";
import { uploadFileToStorage } from "../../api/storage/storage.api";

type UseFileUploadParams = {
  storagePath: string; // Firebase storage path where the file will be uploaded
  fileName?: string;
  autoUpload?: boolean;
  onStart?: () => void;
  onProgress?: (progress?: number) => void;
  onSuccess?: (downloadURL: string) => void;
  onError?: (error: Error) => void;
  transformFile?: (file: File) => Promise<File>; // 파일 받았을 때, 변환작업
};

export const useFileUpload = ({
  storagePath,
  fileName,
  autoUpload,
  onStart,
  onProgress,
  onSuccess,
  onError,
  transformFile,
}: UseFileUploadParams) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [downloadURL, setDownloadURL] = useState<string>("");

  const uploadFile = async (fileToUpload?: File) => {
    const uploadFile = fileToUpload || file;
    if (!uploadFile) return;

    setIsUploading(true);
    try {
      if (onProgress) onProgress();
      const uploadFileName = fileName || uploadFile.name;
      const downloadUrl = await uploadFileToStorage(
        `${storagePath}/${uploadFileName}`,
        uploadFile
      );
      setDownloadURL(downloadUrl);
      setIsUploading(false);
      if (onSuccess) onSuccess(downloadUrl);
    } catch (uploadError) {
      setError(uploadError as Error);
      setIsUploading(false);
      if (onError) onError(uploadError as Error);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      try {
        if (onStart) onStart();
        let processedFile = selectedFile;
        if (transformFile) {
          processedFile = await transformFile(selectedFile);
        }
        setFile(processedFile);
        if (autoUpload) {
          await uploadFile(processedFile);
        }
      } catch (transformError) {
        setError(transformError as Error);
        if (onError) onError(transformError as Error);
      }
    }
  };

  return {
    file,
    isUploading,
    error,
    downloadURL,
    handleFileChange,
    uploadFile,
  };
};
